import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, useMemo, useState } from 'react'

import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  DataframeBody,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useLocalDatasheetColumns } from '@/utils/datasheetColumns/useLocalDatasheetColumns'
import { FuturesColumnsMenu } from '@/pages/futures/FuturesColumnsMenu'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT, TEXT_TRUNCATE } from '@/theme/responsive'
import { useGetReleaseOrderDetailsQuery } from '@/store/api/saleControlApi'
import type { SaleReleaseOrderDetailRow } from '@/store/api/saleControlApi'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

// Access port: frmCtrlSalesQryROAdd_upgrade_detail_subform (btnDetails "Detail (All)").
// READ-ONLY container-detail datasheet of ALL release orders, filtered by the same tab
// filters (Shipment Period + Finalized) — no release_no. Follows /convert-form (compact
// dialog shell + top-right Close) and /convert-table (app-dataframe + Search + per-column
// ▽ filter + Columns menu + pagination + freeze identity col + sort/resize + Access
// parity). No Total footer — the Access subform has no TotalsRow.

const titleBtnSx = { minWidth: 0, px: 1.25, py: 0.25, minHeight: 26, fontSize: '0.75rem', textTransform: 'none' } as const

type ColumnKind = 'text' | 'date' | 'datetime' | 'number' | 'int'
type ColumnDef = {
  key: keyof SaleReleaseOrderDetailRow
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
}

// Column order + list + captions follow the Access datasheet subform
// `frmCtrlSalesQryROAdd_upgrade_detail_subform` (TabIndex order — no ColumnOrder).
// No `package`/`ico_no` columns in Access; no TotalsRow → no footer. Captions verbatim.
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'releaseNo', label: 'ReleaseNo', width: 150 },
  { key: 'invoiceNo', label: 'InvoiceNo', width: 120 },
  { key: 'deliveryOrder', label: 'DeliveryOrder', width: 140 },
  { key: 'containerNo', label: 'ContainerNo', width: 150 },
  { key: 'nwRelease', label: 'Release NW', kind: 'number', align: 'right', width: 120 },
  { key: 'gwRelease', label: 'Release GW', kind: 'number', align: 'right', width: 120 },
  { key: 'bagRelease', label: 'Release BAG', kind: 'number', align: 'right', width: 120 },
  { key: 'dateUpdated', label: 'UpdatedDate', kind: 'datetime', width: 160 },
  { key: 'personUpdated', label: 'UpdatedPerson', width: 140 },
  { key: 'id', label: 'ID', kind: 'int', align: 'right', width: 80 },
]

// Freeze the DeliveryOrder identifier column when scrolling horizontally (/convert-table §7.6).
const FROZEN_KEYS: ReadonlySet<string> = new Set(['deliveryOrder'])
const FROZEN_FALLBACK_WIDTH: Record<string, number> = { deliveryOrder: 140 }

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}
const formatAccessDateTime = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  return `${formatAccessDate(v)} ${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}`
}
const formatAccessNumber = (v: unknown): string => {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (Number.isNaN(n)) return String(v)
  return Number.isInteger(n)
    ? n.toLocaleString('en-US')
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const cellValue = (row: SaleReleaseOrderDetailRow, col: ColumnDef): string => {
  const v = row[col.key]
  if (v == null || v === '') return ''
  if (col.kind === 'date') return formatAccessDate(v)
  if (col.kind === 'datetime') return formatAccessDateTime(v)
  if (col.kind === 'int') return String(Math.round(Number(v))) // integer, no thousand separator
  if (col.kind === 'number') return formatAccessNumber(v)
  return String(v)
}

export default function ReleaseDetailsDialog({
  open,
  shipmentPeriodFrom,
  shipmentPeriodTo,
  finalizedFilter = 'all',
  onClose,
}: {
  open: boolean
  shipmentPeriodFrom?: string
  shipmentPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
  onClose: () => void
}) {
  // "Detail (All)": all release-order details, filtered by the same tab filters
  // (Shipment Period + Finalized) — NOT by a single release_no (Access btnDetails
  // opens the subform with the global filter, no release_no).
  const { data, isLoading, isFetching } = useGetReleaseOrderDetailsQuery(
    { shipmentPeriodFrom, shipmentPeriodTo, finalizedFilter },
    { skip: !open },
  )
  const { getWidth, startResize } = useColumnWidths('salecontrol.release-details')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SaleReleaseOrderDetailRow> | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)
  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: keyof SaleReleaseOrderDetailRow; position: ColumnFilterAnchorPosition } | null>(null)

  // Columns show/hide + reorder menu (/convert-table §10). Local persistence
  // (ships to /user-preferences/column-visibility, no baseline needed).
  const columnsCtrl = useLocalDatasheetColumns({ allColumns: TABLE_COLUMNS, storageKey: 'salecontrol.release-details' })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () => baseRows.filter((r) =>
      rowMatchesSearch(r, searchTerm, TABLE_COLUMNS as never)
      && rowMatchesAllColumnFilters(r, columnFilters.filters),
    ),
    [baseRows, searchTerm, columnFilters.filters],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(
    () => (rowsPerPage === -1 ? sortedRows : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
    [sortedRows, page, rowsPerPage],
  )

  const minTableWidth = useMemo(
    () => visibleColumns.reduce((sum, c) => sum + (getWidth(String(c.key)) ?? c.width ?? 120), 0),
    [visibleColumns, getWidth],
  )
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    searchPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const toggleSort = (key: keyof SaleReleaseOrderDetailRow) => { setSortState((c) => toggleTableSort(c, key)); setPage(0) }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { width: '95vw', maxWidth: 1600 } }}
    >
      <DialogTitle component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Box
          component="span"
          title="Detail (All)"
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
        >
          Detail
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}> (All)</Box>
        </Box>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto', flexShrink: 0 }}>
          <Button type="button" variant="outlined" size="small" sx={titleBtnSx} onClick={onClose}>Close</Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ p: 1, height: '65vh', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
          }}
        >
          <FilterBar hasActiveFilters={hasActiveFilters} onClearAll={clearAllFilters} activeFilterCount={activeFilterCount}>
            <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => searchPopover.open('search', e)} />
            <Button
              size="small"
              variant="outlined"
              endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => openColMenu(e.currentTarget)}
              sx={{ minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }}
              title="Columns"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
            </Button>
          </FilterBar>
          <Divider sx={{ mb: 0.5 }} />

          <Box className="app-dataframe">
            <Box className="app-dataframe-toolbar" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 1, minHeight: 30, py: 0.125 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>Total: {filteredRows.length}</Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={rowsPerPage === -1 ? 0 : page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }}
                rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
                labelRowsPerPage="Rows"
                sx={TABLE_PAGINATION_COMPACT}
              />
            </Box>

            <FilterPopover
              open={searchPopover.state !== null}
              anchorEl={searchPopover.state?.anchor ?? null}
              onClose={() => searchPopover.close()}
            >
              {searchPopover.isOpen('search') && (
                <SearchFilterContent
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Delivery order, container, invoice…"
                  onClear={() => searchPopover.close()}
                />
              )}
            </FilterPopover>

            <ColumnFilterPopover
              open={colPopover !== null}
              anchorPosition={colPopover?.position ?? null}
              onClose={() => setColPopover(null)}
              label={popoverColumn?.label ?? ''}
              kind={popoverKind === 'none' ? 'text' : popoverKind}
              value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
              onChange={(v) => { if (colPopover) columnFilters.setFilter(String(colPopover.key), v) }}
              options={popoverOptions}
            />

            <DataframeBody loading={isLoading} fetching={isFetching}>
              {isLoading ? (
                <BrandState tone="loading" title="Loading…" description="Please wait." />
              ) : rows.length === 0 ? (
                <BrandState tone="empty" title="No release details" description="This release order has no container detail rows." />
              ) : (
                <TableVirtuoso
                  key={gridKey}
                  style={{ height: '100%' }}
                  data={rows}
                  fixedHeaderContent={() => (
                    <TableRow>
                      {visibleColumns.map((col) => {
                        const key = String(col.key)
                        const isFrozen = FROZEN_KEYS.has(key)
                        const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                        const headerStyle: CSSProperties = {}
                        if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                        if (isFrozen) { headerStyle.position = 'sticky'; headerStyle.top = 0; headerStyle.left = 0; headerStyle.zIndex = 100 }
                        return (
                          <TableCell
                            key={key}
                            className="app-table-header-cell"
                            align={col.align}
                            onClick={() => toggleSort(col.key)}
                            style={Object.keys(headerStyle).length ? headerStyle : undefined}
                            sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', ...(isFrozen ? {} : { position: 'relative' }) }}
                          >
                            {col.label}
                            {getTableSortSuffix(sortState, col.key)}
                            <ColumnFilterIcon
                              active={columnFilters.isActive(key)}
                              onClick={(position) => setColPopover({ key: col.key, position })}
                              ariaLabel={`Filter ${col.label}`}
                            />
                            <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )}
                  itemContent={(_: number, row: SaleReleaseOrderDetailRow) =>
                    visibleColumns.map((col) => {
                      const key = String(col.key)
                      const isFrozen = FROZEN_KEYS.has(key)
                      const val = cellValue(row, col)
                      const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                      const wrap = col.wrap || getWidth(key) != null
                      if (wrap) {
                        return (
                          <TableCell
                            key={key}
                            align={col.align}
                            className={isFrozen ? 'app-sticky-col' : undefined}
                            style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                            sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5 }}
                          >
                            <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                          </TableCell>
                        )
                      }
                      return (
                        <TableCell
                          key={key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{val || '-'}</Box>
                        </TableCell>
                      )
                    })
                  }
                  components={{
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    TableHead: ((props: any) => <TableHead {...props} component="thead" />) as never,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as never,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    TableRow: (props: any) => <TableRow {...props} hover />,
                  }}
                />
              )}
            </DataframeBody>
          </Box>
        </Box>
        <FuturesColumnsMenu controller={columnsCtrl} />
      </DialogContent>
    </Dialog>
  )
}
